import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Checkbox, message, InputRef, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined, QrcodeOutlined, CopyOutlined } from '@ant-design/icons';
import { consoleAuthAPI } from '../services/api';
import { setAuth } from '../services/authToken';
import { usePermission } from '../contexts/PermissionContext';

const SAVED_ID_KEY = 'sappt_console_saved_username';

type Step = 'login' | 'enroll' | 'backup' | 'verify';

// 콘솔 전용 다크/레드 팔레트 — lab(indigo 밝은 테마)과 명확히 구분
const RED = '#ef4444';
const RED_DIM = '#b91c1c';
const BG = '#0a0e17';
const PANEL = '#111827';
const BORDER = '#1f2937';
const TEXT = '#e5e7eb';
const MUTED = '#6b7280';

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveId, setSaveId] = useState(false);
  const [time, setTime] = useState(new Date());
  const [step, setStep] = useState<Step>('login');
  const [mfaToken, setMfaToken] = useState('');
  const [enroll, setEnroll] = useState<{ qr_image: string; secret: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const usernameRef = useRef<InputRef>(null);
  const passwordRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const { reload: reloadPermissions } = usePermission();

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(SAVED_ID_KEY);
    if (saved) {
      setSaveId(true);
      form.setFieldsValue({ username: saved });
      setTimeout(() => passwordRef.current?.focus(), 100);
    } else {
      setTimeout(() => usernameRef.current?.focus(), 100);
    }
  }, [form]);

  const errMsg = (e: any, fallback: string) => e?.response?.data?.detail || fallback;

  // 1단계: ID/PW → mfa_pending 토큰
  const onLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const { data } = await consoleAuthAPI.login(values.username, values.password);
      setMfaToken(data.mfa_token);
      if (saveId) localStorage.setItem(SAVED_ID_KEY, values.username);
      else localStorage.removeItem(SAVED_ID_KEY);
      if (data.need_enroll) {
        const setup = await consoleAuthAPI.mfaSetup(data.mfa_token);
        setEnroll({ qr_image: setup.data.qr_image, secret: setup.data.secret });
        setStep('enroll');
      } else {
        setStep('verify');
        setTimeout(() => document.getElementById('mfa-code-input')?.focus(), 120);
      }
    } catch (e: any) {
      message.error(errMsg(e, '아이디 또는 비밀번호가 올바르지 않습니다.'));
    } finally {
      setLoading(false);
    }
  };

  // 최초 등록 확정: 첫 코드 검증 → 백업코드 발급
  const onEnroll = async () => {
    if (code.length < 6) { message.warning('6자리 인증 코드를 입력하세요.'); return; }
    setLoading(true);
    try {
      const { data } = await consoleAuthAPI.mfaEnable(mfaToken, code);
      setBackupCodes(data.backup_codes);
      setCode('');
      setStep('backup');
    } catch (e: any) {
      message.error(errMsg(e, '인증 코드가 올바르지 않습니다.'));
    } finally {
      setLoading(false);
    }
  };

  // 2단계: TOTP/백업코드 검증 → 콘솔 접근 토큰
  const onVerify = async () => {
    if (code.length < 6) { message.warning('인증 코드를 입력하세요.'); return; }
    setLoading(true);
    try {
      const { data } = await consoleAuthAPI.mfaVerify(mfaToken, code);
      setAuth(data.access_token, data.username, false); // 콘솔은 자동로그인 없음(항상 sessionStorage)
      await reloadPermissions();
      message.success(`${data.username}님, 환영합니다.`);
      navigate('/');
    } catch (e: any) {
      const detail = errMsg(e, '인증 코드가 올바르지 않습니다.');
      message.error(detail);
      if (e?.response?.status === 401 && detail.includes('세션')) {
        setStep('login'); setMfaToken(''); setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const goVerifyFromBackup = () => {
    setStep('verify');
    setTimeout(() => document.getElementById('mfa-code-input')?.focus(), 120);
  };

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const formatDate = (d: Date) =>
    d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });

  const label = (t: string) => <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', letterSpacing: 0.3 }}>{t}</span>;
  const darkInput: React.CSSProperties = { background: '#0b1220', borderColor: BORDER, color: TEXT };
  const stepIndex = step === 'login' ? 0 : 1;

  return (
    <div className="console-login" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `radial-gradient(1200px 600px at 50% -10%, #14203a 0%, ${BG} 55%), ${BG}`,
      position: 'relative', overflow: 'hidden', fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
    }}>
      {/* 애니메이션/다크 인풋 스타일 주입 */}
      <style>{`
        .console-login::before{content:'';position:absolute;inset:0;
          background-image:linear-gradient(${BORDER}55 1px,transparent 1px),linear-gradient(90deg,${BORDER}55 1px,transparent 1px);
          background-size:44px 44px;mask-image:radial-gradient(circle at 50% 30%,#000 0%,transparent 70%);opacity:.5;pointer-events:none;}
        .console-login .scan{position:absolute;left:0;right:0;height:120px;pointer-events:none;
          background:linear-gradient(180deg,transparent,${RED}14,transparent);animation:scan 6s linear infinite;}
        @keyframes scan{0%{top:-120px}100%{top:100%}}
        .console-login input{background:#0b1220 !important;color:${TEXT} !important;}
        .console-login .ant-input-affix-wrapper{background:#0b1220 !important;border-color:${BORDER} !important;}
        .console-login .ant-input-affix-wrapper input{background:transparent !important;}
        .console-login input::placeholder{color:#4b5563 !important;}
        .console-login .ant-input-affix-wrapper:focus-within{border-color:${RED} !important;box-shadow:0 0 0 2px ${RED}22 !important;}
        .console-login .ant-checkbox-wrapper{color:${MUTED};}
      `}</style>
      <div className="scan" />

      <div style={{ width: 400, position: 'relative', zIndex: 2 }}>
        {/* 상단 브랜드 */}
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{
            width: 58, height: 58, margin: '0 auto 14px', borderRadius: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${RED_DIM}, #7f1d1d)`,
            boxShadow: `0 8px 30px ${RED}44, inset 0 1px 0 #ffffff22`,
          }}>
            <SafetyCertificateOutlined style={{ fontSize: 28, color: '#fff' }} />
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', letterSpacing: 1 }}>SYSTEM CONSOLE</div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
            padding: '3px 10px', borderRadius: 999, border: `1px solid ${RED}55`,
            background: `${RED}14`, color: '#fca5a5', fontSize: 11, fontWeight: 700, letterSpacing: 1,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: RED, boxShadow: `0 0 8px ${RED}` }} />
            RESTRICTED · MFA REQUIRED
          </div>
        </div>

        {/* 카드 */}
        <div style={{
          background: PANEL, border: `1px solid ${BORDER}`, borderTop: `3px solid ${RED}`,
          borderRadius: 14, padding: '26px 26px 20px', boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}>
          {/* 단계 표시 */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {['ACCOUNT', 'MFA'].map((t, i) => {
              const active = i === stepIndex;
              return (
                <div key={t} style={{ flex: 1 }}>
                  <div style={{ height: 3, borderRadius: 2, background: active ? RED : BORDER }} />
                  <span style={{ fontSize: 10, letterSpacing: 1, color: active ? '#fca5a5' : MUTED, fontWeight: 700 }}>{t}</span>
                </div>
              );
            })}
          </div>

          {step === 'login' && (
            <Form form={form} onFinish={onLogin} size="large" layout="vertical">
              <Form.Item name="username" label={label('아이디')} rules={[{ required: true, message: '아이디를 입력하세요' }]} style={{ marginBottom: 16 }}>
                <Input ref={usernameRef} prefix={<UserOutlined style={{ color: MUTED }} />} placeholder="관리자 아이디" style={darkInput} />
              </Form.Item>
              <Form.Item name="password" label={label('비밀번호')} rules={[{ required: true, message: '비밀번호를 입력하세요' }]} style={{ marginBottom: 14 }}>
                <Input.Password ref={passwordRef} prefix={<LockOutlined style={{ color: MUTED }} />} placeholder="비밀번호" style={darkInput} />
              </Form.Item>
              <Form.Item style={{ marginBottom: 18 }}>
                <Checkbox checked={saveId} onChange={e => setSaveId(e.target.checked)} style={{ fontSize: 12, color: MUTED }}>아이디 저장</Checkbox>
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block danger style={{ height: 44, fontWeight: 700, letterSpacing: 0.5 }}>
                {loading ? '확인 중...' : '다음 →'}
              </Button>
            </Form>
          )}

          {step === 'enroll' && enroll && (
            <div>
              <Alert type="error" showIcon icon={<QrcodeOutlined />} style={{ marginBottom: 14 }}
                message="최초 1회 MFA 등록"
                description="Google Authenticator 앱으로 QR을 스캔한 뒤, 앱의 6자리 코드를 입력하세요." />
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <img src={enroll.qr_image} alt="MFA QR" style={{ width: 172, height: 172, borderRadius: 8, background: '#fff', padding: 8 }} />
              </div>
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>수동 입력 키</div>
                <Typography.Text copyable style={{ color: '#fca5a5', fontFamily: 'monospace', fontSize: 13 }}>{enroll.secret}</Typography.Text>
              </div>
              <Input id="mfa-code-input" size="large" maxLength={6} value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))} onPressEnter={onEnroll}
                placeholder="6자리 코드" style={{ ...darkInput, textAlign: 'center', letterSpacing: 6, fontSize: 18, marginBottom: 12 }} />
              <Button type="primary" danger loading={loading} block style={{ height: 44, fontWeight: 700 }} onClick={onEnroll}>등록 완료</Button>
              <Button type="link" block style={{ marginTop: 6, color: MUTED }} onClick={() => { setStep('login'); setMfaToken(''); setCode(''); }}>← 처음으로</Button>
            </div>
          )}

          {step === 'backup' && (
            <div>
              <Alert type="warning" showIcon style={{ marginBottom: 14 }}
                message="백업 코드를 안전하게 보관하세요"
                description="휴대폰 분실 시 아래 일회용 코드로 로그인합니다. 이 화면을 벗어나면 다시 볼 수 없습니다." />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                {backupCodes.map(c => (
                  <div key={c} style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 14, color: '#fca5a5',
                    background: '#0b1220', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 0' }}>{c}</div>
                ))}
              </div>
              <Button icon={<CopyOutlined />} block style={{ marginBottom: 10, background: '#0b1220', color: TEXT, borderColor: BORDER }}
                onClick={() => { navigator.clipboard?.writeText(backupCodes.join('\n')); message.success('백업 코드가 복사되었습니다.'); }}>
                코드 전체 복사
              </Button>
              <Button type="primary" danger block style={{ height: 44, fontWeight: 700 }} onClick={goVerifyFromBackup}>보관했습니다 · 로그인 계속</Button>
            </div>
          )}

          {step === 'verify' && (
            <div>
              <Alert type="error" showIcon icon={<SafetyCertificateOutlined />} style={{ marginBottom: 14 }}
                message="MFA 인증"
                description="Authenticator 앱의 6자리 코드(또는 백업 코드)를 입력하세요." />
              <Input id="mfa-code-input" size="large" maxLength={8} value={code}
                onChange={e => setCode(e.target.value.replace(/\s/g, ''))} onPressEnter={onVerify}
                placeholder="인증 코드" style={{ ...darkInput, textAlign: 'center', letterSpacing: 6, fontSize: 18, marginBottom: 12 }} />
              <Button type="primary" danger loading={loading} block style={{ height: 44, fontWeight: 700 }} onClick={onVerify}>로그인</Button>
              <Button type="link" block style={{ marginTop: 6, color: MUTED }} onClick={() => { setStep('login'); setMfaToken(''); setCode(''); }}>← 처음으로</Button>
            </div>
          )}

          {/* 하단 상태바 */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${BORDER}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: MUTED }}>
            <span style={{ fontFamily: 'monospace' }}>{formatDate(time)} {formatTime(time)}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <LockOutlined style={{ fontSize: 11, color: '#10b981' }} />
              <span style={{ color: '#34d399', letterSpacing: 0.5 }}>TLS · MFA</span>
            </span>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: '#374151', letterSpacing: 1, fontFamily: 'monospace' }}>
          SAPPT // SECURED SYSTEM CONSOLE
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
